import React from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, makeStyles, Modal as MuiModal, Slide } from '@material-ui/core'
import { noop } from 'lodash'
import clsx from 'clsx'
import { ICON_NAMES, TEXT_VARIANTS } from '../../constants'
import Text from '../atoms/Text'
import Icon from '../atoms/Icon'
import { useToggle } from '../../hooks'

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    padding: '3rem',
    alignItems: ({ position }) => position === 'top' ? 'flex-start' : 'center'
  },
  modalExpanded: {
    alignItems: 'start',
    '& $modalInner': {
      height: '100%',
      width: '100% !important',
      transition: 'width 0.3s ease-in-out'
    }
  },
  modalInner: ({ width }) => ({
    width,
    background: theme.palette.white,
    borderRadius: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    maxWidth: '100%',
    maxHeight: '100%',
    margin: '0 auto',
    transition: 'width 0.3s ease-in-out'
  }),
  modalHeader: {
    padding: '1.75rem 1.7rem 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start'
  },
  modalBody: {
    padding: '1rem 1.75rem 1.75rem',
    overflowY: 'auto'
  }
}))

const Transition = React.forwardRef(function Transition (props, ref) {
  return <Slide direction='up' ref={ref} {...props} />
})
const defaultTransitionDuration = { enter: 200, exit: 200 }

const RoundedModal = ({
  disablePortal = true,
  open = false,
  allowMaximize = false,
  size = 'medium',
  title = '',
  children,
  onClose = noop,
  position
}) => {
  const sizeMap = {
    xsmall: 400,
    small: 600,
    medium: 700,
    large: 1300
  }
  const classes = useStyles({ width: sizeMap[size] ?? sizeMap.medium, position })

  const [isExpanded, toggleIsExpanded] = useToggle(false)

  return (
    <MuiModal
      disablePortal={disablePortal}
      onClose={onClose}
      open={open}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
      disableEnforceFocus
      className={clsx(classes.modal, { [classes.modalExpanded]: isExpanded })}
    >

      <Transition appear in={open} timeout={defaultTransitionDuration} role='none presentation'>
        <div className={classes.modalInner}>
          <div className={classes.modalHeader}>
            <Text variant={TEXT_VARIANTS.h2} customFontSize='1.5rem' text={title} />
            <Box mt='-1rem' display='flex' gridGap='1rem'>
              {allowMaximize && (
                <IconButton
                  onClick={toggleIsExpanded}
                  title={isExpanded ? 'Minimize' : 'Maximize'}
                  edge='end'
                >
                  <Icon customSize='1.2rem' name={isExpanded ? ICON_NAMES.collapse : ICON_NAMES.expand} />
                </IconButton>
              )}
              <IconButton
                onClick={onClose}
                title='Close'
                edge='end'
              >
                <Icon customSize='1.2rem' name={ICON_NAMES.close} />
              </IconButton>
            </Box>
          </div>
          <div className={classes.modalBody}>
            {children}
          </div>
        </div>
      </Transition>
    </MuiModal>
  )
}

RoundedModal.propTypes = {
  disablePortal: PropTypes.bool,
  open: PropTypes.bool,
  allowMaximize: PropTypes.bool,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
  position: PropTypes.oneOf(['top', 'middle']),
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onClose: PropTypes.func
}

RoundedModal.defaultProps = {
  position: 'middle'
}
export default RoundedModal
